import { useEffect, useMemo, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, IconButton, ListSubheader, MenuItem, SxProps } from '@mui/material'
import { Bank, type IBank } from '@/@data'
import { useRouteQuery, useTranslator, useDebounce } from '@/hooks'
import { InputField } from '../common'

interface BankSelectionProps {
  value?: string | string[] | number | number[]
  multiple?: boolean
  onChange?: (value: string, selected?: IBank) => void
  disabled?: boolean
  required?: boolean
  label?: string
  placeholder?: string
  className?: string
  fullWidth?: boolean
  onClear?: () => void
  height?: string | number
  error?: boolean
  helperText?: string
  sx?: SxProps
  showEndAdornment?: boolean
  wideEndAdornment?: boolean // NEW PROP
}

const BankSelection = ({
  value = '',
  multiple = false,
  onChange,
  onClear,
  disabled = false,
  label = '',
  placeholder = '',
  error = false,
  helperText = '',
  required = false,
  showEndAdornment = true,
  wideEndAdornment = false, // NEW PROP
  className,
  sx
}: BankSelectionProps) => {
  const { serializeQuery } = useRouteQuery()
  const [search, setSearch] = useState('')
  const { __ } = useTranslator()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const debouncedSearch = useDebounce(search, 500)

  const { data } = useQuery({
    ...Bank.objects.paginateQueryFactory(
      serializeQuery({
        keywords: debouncedSearch
      })
    )
  })

  const { data: selectedBranches } = useQuery({
    ...Bank.objects.paginateQueryFactory(
      serializeQuery({
        ids: Array.isArray(value) ? value : [value]
      })
    ),
    enabled: !!value && (Array.isArray(value) ? value.length > 0 : true)
  })

  useEffect(() => {
    if (data) {
      setTimeout(() => searchInputRef.current?.focus())
    }
  }, [data])

  const options = useMemo(() => {
    const paginateOptions = data ? Bank.getBanksSelectOptions(data.data) : []
    const selectedOptions = selectedBranches ? Bank.getBanksSelectOptions(selectedBranches.data) : []

    const combinedOptions = [...paginateOptions]

    selectedOptions.forEach(option => {
      if (!combinedOptions.some(opt => opt.value === option.value)) {
        combinedOptions.push(option)
      }
    })

    return combinedOptions
  }, [data, selectedBranches])

  return (
    <InputField
      select
      value={value || (multiple ? [] : '')}
      label={
        required ? (
          <>
            {label}
            <span style={{ color: 'red' }}>*</span>
          </>
        ) : (
          label
        )
      }
      onChange={e =>
        onChange?.(
          e.target.value,
          data?.data.find(b => b.id === Number(e.target.value))
        )
      }
      fullWidth
      error={error}
      helperText={helperText}
      disabled={disabled}
      className={[className, showEndAdornment && wideEndAdornment ? 'bank-wide-end-adornment' : '']
        .filter(Boolean)
        .join(' ')}
      sx={sx}
      slotProps={{
        input: {
          endAdornment:
            showEndAdornment &&
            onClear &&
            value &&
            ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value)) ? (
              <Box mr={5}>
                <IconButton
                  size='small'
                  onClick={() => {
                    onClear()
                  }}
                  aria-label='clear selection'
                  tabIndex={-1}
                >
                  <i className='tabler-x text-[16px] text-textPrimary'></i>
                </IconButton>
              </Box>
            ) : null
        },
        select: {
          multiple,
          displayEmpty: true,
          renderValue: (selected: any) => {
            if (!selected || (Array.isArray(selected) && selected.length === 0)) {
              return <span style={{ color: '#aaa' }}>{placeholder || label || __('Select company')}</span>
            }

            if (multiple && Array.isArray(selected)) {
              const selectedLabels = options
                .filter(option => selected.map(Number).includes(Number(option.value)))
                .map(option => option.label)
                .join(', ')

              return <>{selectedLabels}</>
            }

            const found = options.find(option => Number(option.value) === Number(selected))

            return <>{found ? found.shortName + ' - ' + found.label : selected}</>
          }
        }
      }}
    >
      <ListSubheader onKeyDown={e => e.stopPropagation()}>
        <InputField
          fullWidth
          defaultValue={search}
          placeholder={__('Search Name/Code')}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.stopPropagation()}
          inputRef={searchInputRef}
        />
      </ListSubheader>
      {options.map(option => (
        <MenuItem onKeyDown={e => e.stopPropagation()} key={option.value} value={option.value}>
          {option.shortName} - {option.label}
        </MenuItem>
      ))}
    </InputField>
  )
}

export default BankSelection
